import { useContext, useEffect, useRef, useState } from 'react';
import styles from './Tags.module.css';
import AIEduDataTable from '../../components/DataTable/AIEduDataTable';
import AIEduSearch from '../../components/Search/AIEduSearch';
import AIEduPaginator from '../../components/Paginator/AIEduPaginator';
import AIEduButton from '../../components/Button/AIEduButton';
import AIEduSpinner from '../../components/Spinner/AIEduSpinner';
import AIEduToast from '../../components/Toast/AIEduToast';
import { API } from '../../services';
import { useAuthenticate } from '../../hooks';
import { Context } from '../../context/Context';
import {
    setToastComponent,
    showSuccessToast,
} from '../../services/setup/ApiResponseHandler';
import DeleteTagDialog from './DeleteTagDialog';
import { useIntl } from 'react-intl';
import { copyToClipboard, formatDateWithTimeStamp } from '../../utils/Utils';
import { ASC, DESC } from '../../utils/Constants';
import PageTitle from '../../layout/PageTitle/pageTitle';
import { AddTagPopUp, TagCreatedPopUp } from './TagPopups';
import { useNavigate } from 'react-router-dom';
import { TAG_MANAGEMENT_PATH } from '../../router/RouteConstants';

const TagManagement = () => {
    const intl = useIntl();
    const { formatMessage: f } = intl;
    const navigate = useNavigate();

    const { accountInfo } = useAuthenticate();
    const { organizations, place } = accountInfo ?? {};
    const { placeId } = place ?? {};
    const { organizationId } = organizations?.[0] ?? {};

    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const { refreshMap } = useContext(Context);

    const [searchTerm, setSearchTerm] = useState('');
    const toastRef = useRef(null);
    const [tags, setTags] = useState([]);
    const [tagsetId, setTagsetId] = useState('');
    const [tagsetCategoryId, setTagsetCategoryId] = useState('');
    const [selectedTagList, setSelectedTagList] = useState([]);
    const [visible, setVisible] = useState(false);
    const [deleteAction, setDeleteAction] = useState(null);
    const [deleteMessage, setDeleteMessage] = useState('');

    const [isAddTagPopupVisible, setIsAddTagPopupVisible] = useState(false);
    const [tagCreatedPopupVisible, setTagCreatedPopupVisible] = useState(false);
    const [createdTag, setCreatedTag] = useState({
        tagId: '',
        tagName: '',
        webHookUrl: '',
        password: '',
    });

    const [sortField, setSortField] = useState('updatedTime');
    const [sortOrder, setSortOrder] = useState(-1);

    const onPageChange = (e) => {
        const page = e.page;
        const size = e.rows;

        setFirst(e.first);
        setRows(size);
        setPage(page);
        setSelectedTagList([]);

        getAllTagsDetails(page, size, sortField, sortOrder, searchTerm);
    };

    const getAllTagsDetails = (start, rowsPerPage, sortBy, sort, search) => {
        setLoading(true);
        const params = {
            start,
            rowsPerPage,
            sortBy: sortBy || 'updatedTime',
            sort: sort === 1 ? ASC : DESC,
            search,
        };
        API.getAllTags(organizationId, placeId, params)
            .then(({ data }) => {
                const { total, sortBy, sort } = data ?? {};
                const { tagsetId, tagsetCategoryId, tags } = data.data ?? {};
                if (tags?.length === 0 && start > 0 && total > 0) {
                    setFirst(0);
                    setPage(0);
                    getAllTagsDetails(
                        0,
                        rowsPerPage,
                        sortBy,
                        sortOrder,
                        search
                    );
                } else {
                    const tagList = tags?.map((tag) => {
                        const {
                            tagId,
                            tagName,
                            webHookUrl,
                            password,
                            updatedTime,
                        } = tag ?? {};
                        return {
                            tagId,
                            tagName,
                            webHookUrl,
                            password,
                            updatedTime,
                        };
                    });
                    setTags(tagList);
                    setTagsetId(tagsetId);
                    setTagsetCategoryId(tagsetCategoryId);
                    setTotalRecords(total);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const resetPageParams = () => {
        setFirst(0);
        setPage(0);
        setSortField('updatedTime');
        setSortOrder(-1);
        setSearchTerm('');
        setSelectedTagList([]);
    };

    useEffect(() => {
        setToastComponent(toastRef.current);
    }, []);

    useEffect(() => {
        resetPageParams();
        getAllTagsDetails(0, rows);
    }, [refreshMap.tag]);

    const actionBodyTemplate = (rowData) => {
        const { tagId } = rowData ?? {};
        const confirmSingleDelete = (e) => {
            e.stopPropagation();

            setDeleteMessage(f({ id: 'AI_EDU_DELETE_TAG_TEXT' }));
            setDeleteAction(() => () => {
                handleDeleteTagList([tagId]);
            });
            setVisible(true);
        };
        return (
            <i
                className='pi pi-trash cursor-pointer'
                style={{ fontSize: '20px' }}
                onClick={confirmSingleDelete}
            />
        );
    };

    const tagNameTemplate = (rowData) => {
        const handleClick = (e) => {
            e.stopPropagation();
        };

        return <span onClick={handleClick}>{rowData.tagName}</span>;
    };

    const webHookUrlTemplate = (rowData) => {
        const handleClick = (e) => {
            e.stopPropagation();
        };

        return <span onClick={handleClick}>{rowData.webHookUrl}</span>;
    };

    const apiKeyTemplate = (rowData) => {
        const copyApiKey = (e) => {
            e.stopPropagation();

            copyToClipboard(rowData.password);
            showSuccessToast('Copied');
        };
        return (
            <i
                className='pi pi-key cursor-pointer'
                style={{ fontSize: '20px' }}
                onClick={copyApiKey}
            />
        );
    };

    const updatedTimeTemplate = (rowData) => {
        const handleClick = (e) => {
            e.stopPropagation();
        };

        return (
            <span onClick={handleClick}>
                {!!rowData.updatedTime
                    ? formatDateWithTimeStamp(rowData.updatedTime)
                    : '--'}
            </span>
        );
    };

    const columns = [
        {
            field: 'tagName',
            header: f({ id: 'AI_EDU_TAG_NAME' }),
            body: tagNameTemplate,
            width: '20%',
            minWidth: '15rem',
            sortable: true,
        },
        {
            field: 'webHookUrl',
            header: f({ id: 'AI_EDU_TAG_WEBHOOK_URL' }),
            body: webHookUrlTemplate,
            width: '40%',
            minWidth: '20rem',
        },
        {
            field: 'password',
            header: f({ id: 'AI_EDU_TAG_API_KEY' }),
            body: apiKeyTemplate,
            width: '10%',
            minWidth: '5rem',
        },
        {
            field: 'updatedTime',
            header: f({ id: 'AI_EDU_UPDATED_DATE' }),
            body: updatedTimeTemplate,
            width: '20%',
            minWidth: '10rem',
            sortable: true,
        },
        {
            header: f({ id: 'AI_EDU_ACTION' }),
            body: actionBodyTemplate,
            minWidth: '5rem',
        },
    ];

    const handleDeleteTagList = (tagList) => {
        API.deleteTags(organizationId, placeId, tagList)
            .then((res) => {
                if (res.status === 200) {
                    setSelectedTagList((prev) =>
                        prev.filter((tag) => !tagList.includes(tag.tagId))
                    );
                    if (tagList === tags) {
                        setPage(Math.max(0, page - 1));
                    }
                    getAllTagsDetails(
                        tagList === tags ? Math.max(0, page - 1) : page,
                        rows,
                        sortField,
                        sortOrder,
                        searchTerm
                    );
                }
            })
            .finally(() => {
                setVisible(false);
                setDeleteMessage('');
            });
    };

    const handleMultipleTagsDelete = () => {
        setDeleteMessage(f({ id: 'AI_EDU_DELETE_TAG_TEXT' }));
        const tags = selectedTagList?.map((tags) => tags.tagId);
        setDeleteAction(() => () => handleDeleteTagList(tags));
        setVisible(true);
    };

    const handleTagSearch = () => {
        setFirst(0);
        setPage(0);
        setSelectedTagList([]);
        getAllTagsDetails(0, rows, sortField, sortOrder, searchTerm);
    };

    const handleInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleTagSearch();
        }
    };

    const handleAddTag = () => {
        setIsAddTagPopupVisible(true);
    };

    const onRowClick = (e) => {
        navigate(`${TAG_MANAGEMENT_PATH}/${e.data.tagId}`);
    };

    const onSort = (e) => {
        const { field, order } = { field: e.sortField, order: e.sortOrder };

        getAllTagsDetails(
            page,
            rows,
            field,
            order,
            !!searchTerm ? searchTerm : null
        );

        setSortField(field);
        setSortOrder(order);
    };

    const sortIcon = (options) => {
        if (options.sortOrder === 1) {
            return (
                <i
                    className='pi pi-sort-up-fill'
                    style={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                />
            );
        }
        if (options.sortOrder === -1) {
            return (
                <i
                    className='pi pi-sort-down-fill'
                    style={{ marginLeft: '0.5rem', marginTop: '0.2rem' }}
                />
            );
        }
    };

    return (
        <>
            {loading && <AIEduSpinner />}
            <DeleteTagDialog
                visible={visible}
                hideDeleteTagsDialog={() => setVisible(!visible)}
                deleteSelectedTags={deleteAction}
                deleteMessage={deleteMessage}
            />
            <AIEduToast ref={toastRef} />
            <div className={styles.tags}>
                <PageTitle
                    title={f({ id: 'AI_EDU_TAG_MANAGEMENT' })}
                    count={totalRecords}
                />
                <div className='mb-4 flex justify-content-between flex-wrap gap-3'>
                    <AIEduSearch
                        value={searchTerm}
                        setValue={setSearchTerm}
                        handleInputKeyDown={handleInputKeyDown}
                        placeholder={f({ id: 'AI_EDU_SEARCH_TAG' })}
                        searchHandler={handleTagSearch}
                    />
                    <div className='flex align-items-center'>
                        <AIEduButton
                            icon='pi pi-trash'
                            className={styles.tags_delete}
                            onClick={handleMultipleTagsDelete}
                            disabled={selectedTagList.length === 0}
                        />
                        <div className={`${styles.vertical_divider} mx-3`} />
                        <AIEduButton
                            label={f({ id: 'AI_EDU_ADD_TAG' })}
                            icon='pi pi-plus'
                            className='btnPrimary'
                            onClick={handleAddTag}
                        />
                    </div>
                </div>
                <div
                    className='mb-2'
                    style={{
                        height: '1rem',
                        fontFamily: 'SamsungOne-700, sans-serif',
                    }}
                >
                    {selectedTagList?.length > 0 && (
                        <span>
                            {selectedTagList.length} of {tags?.length} selected
                        </span>
                    )}
                </div>
                <AIEduDataTable
                    data={tags}
                    columns={columns}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    sortIcon={sortIcon}
                    onSort={onSort}
                    lazy={true}
                    dataKey='tagId'
                    selection={selectedTagList}
                    onSelectionChange={(e) => setSelectedTagList(e.value)}
                    onRowClick={onRowClick}
                    rowClassName={styles.tags_row}
                    scrollHeight='60vh'
                    selectionMode='multiple'
                    emptyMessage={f({ id: 'AI_EDU_NO_DATA_TO_SHOW' })}
                />

                {tags?.length > 0 && (
                    <AIEduPaginator
                        rows={rows}
                        first={first}
                        totalRecords={totalRecords}
                        onPageChange={onPageChange}
                    />
                )}

                <AddTagPopUp
                    setTag={setCreatedTag}
                    tagsetId={tagsetId}
                    tagsetCategoryId={tagsetCategoryId}
                    isAddTagPopupVisible={isAddTagPopupVisible}
                    setIsAddTagPopupVisible={setIsAddTagPopupVisible}
                    setTagCreatedPopupVisible={setTagCreatedPopupVisible}
                />
                <TagCreatedPopUp
                    tag={createdTag}
                    tagCreatedPopupVisible={tagCreatedPopupVisible}
                    setTagCreatedPopupVisible={setTagCreatedPopupVisible}
                />
            </div>
        </>
    );
};

export default TagManagement;
